// AT-IA-1b (2026-06-05) — Modal de importar ART de PDF (IA preenche os campos).
// Aciona action op_art_extrair_pdf no backend. Mostra preview lado-a-lado
// (atual × extraido) e permite escolher quais campos aplicar.

import { useState } from 'react';
import axios from 'axios';
import {
  Modal, Button, Upload, Space, Typography, Alert, Table, Checkbox, Tag,
  Spin, message, App as AntApp, Tooltip, Empty, Radio,
} from 'antd';
import {
  UploadOutlined, FileTextOutlined, RobotOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from './utils';

const { Text, Paragraph } = Typography;

// Rotulos amigaveis pra cada chave do JSON retornado pela IA
const LABELS: Record<string, string> = {
  numero: 'Numero ART',
  tipo_art: 'Tipo de ART',
  finalidade: 'Finalidade',
  motivo_art: 'Motivo',
  participacao_tecnica: 'Participacao tecnica',
  convenio: 'Convenio',
  entidade_classe: 'Entidade de classe',
  rt_nome: 'RT — Nome',
  rt_carteira: 'RT — Carteira CREA',
  rt_rnp: 'RT — RNP',
  rt_titulo_profissional: 'RT — Titulo profissional',
  rt_email: 'RT — Email',
  contratante_nome: 'Contratante — Razao social',
  contratante_cnpj: 'Contratante — CNPJ',
  contratante_eh_orgao_publico: 'Contratante — E orgao publico',
  contratante_telefone: 'Contratante — Telefone',
  contratante_cep: 'Contratante — CEP',
  contratante_logradouro: 'Contratante — Logradouro',
  contratante_numero: 'Contratante — Numero',
  contratante_complemento: 'Contratante — Complemento',
  contratante_bairro: 'Contratante — Bairro',
  contratante_municipio: 'Contratante — Municipio',
  contratante_uf: 'Contratante — UF',
  proprietario_nome: 'Proprietario — Razao social',
  proprietario_cnpj: 'Proprietario — CNPJ',
  empresa_executante_nome: 'Empresa executante',
  empresa_executante_nr_reg: 'Empresa executante — Reg CREA',
  inicio_art: 'Inicio da ART',
  fim_art: 'Fim da ART',
  valor: 'Valor',
  honorarios: 'Honorarios',
  obra_cep: 'Obra — CEP',
  obra_tipo_logradouro: 'Obra — Tipo logradouro',
  obra_logradouro: 'Obra — Logradouro',
  obra_numero: 'Obra — Numero',
  obra_complemento: 'Obra — Complemento',
  obra_bairro: 'Obra — Bairro',
  obra_municipio: 'Obra — Municipio',
  obra_uf: 'Obra — UF',
  resumo_contrato: 'Resumo do contrato',
  observacao: 'Observacao',
  atividades: 'Atividades (lista)',
};

interface Props {
  open: boolean;
  contratoId: string;
  artId?: string;                 // se editando, manda pra calcular diff
  formAtual?: Record<string, any>; // valores atuais do form pra comparar
  onCancel: () => void;
  onAplicar: (payload: {
    campos: Record<string, any>;
    linhas_atividade: any[];      // AT-IA-2: linhas mapeadas pro catalogo
    pdf_s3_key: string;           // S3 key do PDF importado (anexa no form)
    status_pdf: 'rascunho_interno' | 'preenchida' | 'registrada' | 'paga';
  }) => void;
}

function ModalImportarArtPdfInner({
  open, contratoId, artId, formAtual, onCancel, onAplicar,
}: Props) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [statusPdf, setStatusPdf] = useState<'rascunho_interno' | 'preenchida' | 'registrada' | 'paga'>('rascunho_interno');
  const [processando, setProcessando] = useState(false);
  const [resultado, setResultado] = useState<any | null>(null);
  const [aplicar, setAplicar] = useState<Record<string, boolean>>({});

  function reset() {
    setArquivo(null);
    setResultado(null);
    setAplicar({});
  }

  async function processar() {
    if (!arquivo) {
      message.warning('Selecione um PDF');
      return;
    }
    setProcessando(true);
    try {
      const b64 = await fileToBase64(arquivo);
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        {
          action: 'op_art_extrair_pdf',
          pdf_base64: b64,
          contrato_id: contratoId,
          art_id: artId,
        },
        { headers: { Authorization: token }, timeout: 60000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setResultado(d);
      // Por default marca todos como "aplicar" — usuario desmarca o que nao
      // quer
      const map: Record<string, boolean> = {};
      for (const k of Object.keys(d.campos_extraidos || {})) {
        map[k] = true;
      }
      setAplicar(map);
      message.success(`IA extraiu ${Object.keys(d.campos_extraidos || {}).length} campos`);
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message;
      message.error('Falha ao importar: ' + msg);
    } finally { setProcessando(false); }
  }

  function aplicarSelecao() {
    if (!resultado?.campos_extraidos) return;
    const aplicar_campos: Record<string, any> = {};
    for (const [k, v] of Object.entries(resultado.campos_extraidos)) {
      if (aplicar[k]) aplicar_campos[k] = v;
    }
    // Linhas de atividade mapeadas (AT-IA-2) so aplica se atividades estava
    // marcado no checkmap — comportamento esperado: usuario controla via 1
    // checkbox "atividades"
    const linhas = (aplicar.atividades && Array.isArray(resultado.linhas_atividade))
      ? resultado.linhas_atividade
      : [];
    onAplicar({
      campos: aplicar_campos,
      linhas_atividade: linhas,
      pdf_s3_key: resultado.s3_key,
      status_pdf: statusPdf,
    });
    reset();
  }

  // Tabela de comparacao atual × extraido
  const linhas = resultado?.campos_extraidos
    ? Object.entries(resultado.campos_extraidos).map(([k, v]) => {
        const atual = formAtual?.[k];
        const igual = atual != null && String(atual).trim() === String(v).trim();
        return {
          key: k,
          campo: LABELS[k] || k,
          atual,
          extraido: v,
          igual,
        };
      })
    : [];
  // Ordena: diferentes primeiro
  linhas.sort((a, b) => Number(a.igual) - Number(b.igual));

  function fmtValor(v: any): string {
    if (v == null || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Sim' : 'Nao';
    if (Array.isArray(v)) return v.map(String).join(' • ');
    return String(v);
  }

  return (
    <Modal open={open} onCancel={() => { reset(); onCancel(); }}
      title={<Space><RobotOutlined /> Importar ART de PDF (IA Haiku)</Space>}
      width={resultado ? 900 : 560}
      footer={
        resultado
          ? [
              <Button key="c" onClick={() => { reset(); onCancel(); }}>Cancelar</Button>,
              <Button key="n" onClick={reset}>Outro arquivo</Button>,
              <Button key="a" type="primary" onClick={aplicarSelecao}
                disabled={Object.values(aplicar).every(v => !v)}>
                Aplicar selecionados ({Object.values(aplicar).filter(Boolean).length})
              </Button>,
            ]
          : [
              <Button key="c" onClick={onCancel}>Cancelar</Button>,
              <Button key="p" type="primary" loading={processando} onClick={processar}
                disabled={!arquivo}>
                Processar PDF
              </Button>,
            ]
      }
      destroyOnClose>

      {!resultado && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert type="info" showIcon
            message="Cole o PDF da ART do CREA-RS"
            description="O sistema vai extrair o texto do PDF e usar IA pra preencher os campos da ART. Voce vai poder revisar antes de aplicar." />

          <Upload accept=".pdf,application/pdf" maxCount={1}
            beforeUpload={(file) => {
              setArquivo(file);
              return false;  // nao faz upload Antd, a gente trata
            }}
            onRemove={() => setArquivo(null)}
            fileList={arquivo ? [{
              uid: '1', name: arquivo.name, status: 'done' as const,
            }] : []}>
            <Button icon={<UploadOutlined />}>Selecionar PDF</Button>
          </Upload>

          {/* Status da ART neste PDF — vai virar anexo + altera status_crea */}
          <div style={{ background: '#fafafa', padding: 10, borderRadius: 4 }}>
            <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
              Esse PDF e:
            </Text>
            <Radio.Group value={statusPdf} onChange={e => setStatusPdf(e.target.value)}>
              <Space direction="vertical" size={4}>
                <Radio value="rascunho_interno">
                  <Text style={{ fontSize: 12 }}>Rascunho interno</Text>
                  <Text type="secondary" style={{ fontSize: 10, marginLeft: 8 }}>
                    (anexa em "Rascunho", status_crea=rascunho_interno)
                  </Text>
                </Radio>
                <Radio value="preenchida">
                  <Text style={{ fontSize: 12 }}>Preenchida no portal CREA</Text>
                  <Text type="secondary" style={{ fontSize: 10, marginLeft: 8 }}>
                    (anexa em "Rascunho", status_crea=preenchida)
                  </Text>
                </Radio>
                <Radio value="registrada">
                  <Text style={{ fontSize: 12 }}>Registrada no CREA</Text>
                  <Text type="secondary" style={{ fontSize: 10, marginLeft: 8 }}>
                    (anexa em "Assinada", status_crea=registrada)
                  </Text>
                </Radio>
                <Radio value="paga">
                  <Text style={{ fontSize: 12 }}>Paga / Definitiva</Text>
                  <Text type="secondary" style={{ fontSize: 10, marginLeft: 8 }}>
                    (anexa em "Assinada", status_crea=paga)
                  </Text>
                </Radio>
              </Space>
            </Radio.Group>
          </div>

          {processando && (
            <Space direction="vertical" align="center" style={{ width: '100%', padding: 20 }}>
              <Spin size="large" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Extraindo texto + IA processando... (~10-15s)
              </Text>
            </Space>
          )}
        </Space>
      )}

      {resultado && (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Alert type="success" showIcon
            message={`IA extraiu ${Object.keys(resultado.campos_extraidos || {}).length} campos do PDF${
              Array.isArray(resultado.linhas_atividade) && resultado.linhas_atividade.length
                ? ` + ${resultado.linhas_atividade.length} linhas de atividade mapeadas no catalogo`
                : ''
            }`}
            description={
              <Space size={16} wrap>
                <Tag color="blue">{resultado.paginas} paginas</Tag>
                <Tag>{resultado.texto_chars} chars</Tag>
                <Tag color="purple">
                  Tokens IA: {resultado.tokens_in}/{resultado.tokens_out}
                </Tag>
                {Array.isArray(resultado.linhas_atividade) && resultado.linhas_atividade.length > 0 && (
                  <Tag color="cyan">{resultado.linhas_atividade.length} linhas</Tag>
                )}
              </Space>
            } />

          <Text type="secondary" style={{ fontSize: 11 }}>
            Desmarque os campos que voce NAO quer aplicar.
            {!!artId && ' Campos com valor diferente do atual ficam destacados.'}
          </Text>

          {linhas.length === 0 ? (
            <Empty description="IA nao identificou nenhum campo conhecido" />
          ) : (
            <Table size="small" rowKey="key" dataSource={linhas} pagination={false}
              scroll={{ y: 480 }}
              columns={[
                {
                  title: '', width: 40, dataIndex: 'key',
                  render: (k: string) => (
                    <Checkbox checked={!!aplicar[k]}
                      onChange={e => setAplicar({ ...aplicar, [k]: e.target.checked })} />
                  ),
                },
                {
                  title: 'Campo', dataIndex: 'campo', width: 220,
                  render: (v: string) => <Text strong style={{ fontSize: 12 }}>{v}</Text>,
                },
                ...(artId ? [{
                  title: 'Atual', dataIndex: 'atual', width: 200,
                  render: (v: any) => (
                    <Text style={{ fontSize: 11, color: '#888' }}>{fmtValor(v)}</Text>
                  ),
                }] : []),
                {
                  title: artId ? 'Extraido (IA)' : 'Valor extraido',
                  dataIndex: 'extraido',
                  render: (v: any, row: any) => (
                    <Text style={{
                      fontSize: 12,
                      color: artId && !row.igual ? '#020D7F' : '#222',
                      fontWeight: artId && !row.igual ? 600 : 400,
                    }}>
                      {fmtValor(v)}
                      {artId && !row.igual && (
                        <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>
                          alterado
                        </Tag>
                      )}
                    </Text>
                  ),
                },
              ] as any} />
          )}
        </Space>
      )}
    </Modal>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      // Remove prefix data:application/pdf;base64,
      const idx = s.indexOf(',');
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ModalImportarArtPdf(props: Props) {
  return <AntApp><ModalImportarArtPdfInner {...props} /></AntApp>;
}
